import React, { useState } from "react";
import { ExternalLink, ArrowUpRight } from "lucide-react";
import SentimentBadge from "@/components/SentimentBadge";
import DefaultNewsImage from "@/components/DefaultNewsImage";
import { timeAgo } from "@/lib/api";
import { topicImageUrl } from "@/lib/topicImage";

/**
 * NewsImage tries three sources in order:
 *   1. The article's own image_url (from the RSS feed) — unless it's a known
 *      publisher placeholder logo (e.g. Business Standard's generic BS image
 *      served for image-less articles).
 *   2. A topical stock/news photo from loremflickr, based on title keywords.
 *   3. An inline SVG hero (NiftyNews-branded gradient) — never fails.
 */

// URL patterns known to be publisher fallback logos, not actual article art.
// Matched case-insensitively; if the article's image_url contains any of
// these substrings we treat it as "no image" and jump to the topical image.
const PLACEHOLDER_PATTERNS = [
    "business-standard.com/assets/web-assets/images/business_standard",
    "bsmedia.business-standard.com/_media/bs/img/logo",
    "moneycontrol.com/images/common/mc-logo",
    "moneycontrol.com/mccode/common/mc-brand",
    "livemint.com/lm-img/img/2021/09/14/",
    "livemint.com/img/mintlogo",
    "economictimes.indiatimes.com/photo/msid-",  // often generic
    "ndtvimg.com/assets/img/ndtv_profit",
    "financialexpress.com/wp-content/uploads/2020/01/fe-logo",
    "media.assettype.com/theken/",
    "seekingalpha.com/img/logo",
    "/logo.png",
    "/logo.jpg",
    "/logo.svg",
    "_logo.png",
    "_logo.jpg",
    "/default.jpg",
    "/default.png",
    "/placeholder.jpg",
    "/placeholder.png",
];

function isPlaceholderImage(url) {
    if (!url) return true;
    const lower = String(url).toLowerCase();
    return PLACEHOLDER_PATTERNS.some((p) => lower.includes(p));
}

const NewsImage = ({ item, className = "", featured = false }) => {
    const rawUrl = item?.image_url || null;
    const usableUrl = rawUrl && !isPlaceholderImage(rawUrl) ? rawUrl : null;
    const secondary = topicImageUrl(item);
    const [stage, setStage] = useState(usableUrl ? 0 : 1);
    const label = (item?.region || "").toUpperCase() || "STOCK NEWS";
    const seed = item?.id || item?.title;

    if (stage === 2) {
        return (
            <div className={className}>
                <DefaultNewsImage seed={seed} label={label} />
            </div>
        );
    }
    const src = stage === 0 ? usableUrl : secondary;
    return (
        <div className={className}>
            <img
                src={src}
                alt=""
                loading="lazy"
                decoding="async"
                fetchPriority={featured ? "high" : "low"}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={() => setStage((s) => s + 1)}
                data-testid={stage === 0 ? "news-img-primary" : stage === 1 ? "news-img-topical" : "news-img-default"}
            />
        </div>
    );
};

const NewsCard = ({ item, featured = false, testId }) => {
    const { title, summary, url, source, pub_date, sentiment, region } = item;

    if (featured) {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="news-card group block"
                data-testid={testId}
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-3 relative aspect-[16/10] lg:aspect-auto lg:min-h-[380px] bg-slate-800 overflow-hidden">
                        <NewsImage item={item} className="w-full h-full" featured />
                        <div className="absolute top-4 left-4 font-sub text-[10px] uppercase tracking-[0.22em] text-white/85 bg-black/40 backdrop-blur-sm px-2.5 py-1.5 rounded-sm">
                            Featured · {region === "global" ? "Global" : "India"}
                        </div>
                    </div>
                    <div className="lg:col-span-2 p-6 lg:p-8 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-3 flex-wrap mb-4">
                                <SentimentBadge sentiment={sentiment} />
                                <span className="font-sub text-[10px] uppercase tracking-[0.18em] text-white/50 flex items-center gap-1.5">
                                    <span className="truncate max-w-[180px]">{source}</span>
                                    <span className="w-1 h-1 rounded-full bg-white/25" />
                                    <span>{timeAgo(pub_date)}</span>
                                </span>
                            </div>
                            <h2 className="font-heading text-[26px] sm:text-[32px] lg:text-[36px] leading-[1.05] font-bold text-white group-hover:text-white/95">
                                {title}
                            </h2>
                            {summary && (
                                <p className="mt-4 font-body text-[15px] text-white/60 leading-relaxed line-clamp-3">
                                    {summary}
                                </p>
                            )}
                        </div>
                        <div className="mt-6 flex items-center gap-2 font-sub text-[11px] uppercase tracking-[0.2em] text-white/70 group-hover:text-white">
                            Read the full story
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                    </div>
                </div>
            </a>
        );
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="news-card group h-full flex flex-col"
            data-testid={testId}
        >
            <div className="aspect-[16/9] bg-slate-800 overflow-hidden relative">
                <NewsImage item={item} className="w-full h-full" />
            </div>
            <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                    <SentimentBadge sentiment={sentiment} size="sm" />
                    <span className="font-sub text-[9px] uppercase tracking-[0.18em] text-white/45 flex items-center gap-1.5">
                        <span className="truncate max-w-[120px]">{source}</span>
                        <span className="w-1 h-1 rounded-full bg-white/25" />
                        <span>{timeAgo(pub_date)}</span>
                    </span>
                </div>
                <h3 className="font-heading text-[18px] leading-tight font-semibold text-white line-clamp-3 group-hover:text-white/95">
                    {title}
                </h3>
                {summary && (
                    <p className="mt-2 font-body text-[13px] text-white/50 line-clamp-2 leading-relaxed">
                        {summary}
                    </p>
                )}
                <div className="mt-auto pt-3 flex items-center justify-between text-white/40 group-hover:text-white/85 transition-colors">
                    <span className="font-sub text-[10px] uppercase tracking-[0.2em]">Read article</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                </div>
            </div>
        </a>
    );
};

export default NewsCard;
